import type { ExamBoardConfig, ExamBoardId, ScoringScale } from "./types";

/**
 * Shared UAT-UK scoring philosophy: 1.0–9.0 per module/paper, MCQ, no negative
 * marking, no calculator. Both TMUA and ESAT inherit this; overrides per board
 * if they ever diverge.
 */
const UAT_UK_SCORING: ScoringScale = {
  min: 1.0,
  max: 9.0,
  step: 0.1,
  negativeMarking: false,
  calculatorAllowed: false,
};

const TMUA: ExamBoardConfig = {
  id: "TMUA",
  slug: "tmua",
  name: "Test of Mathematics for University Admission",
  shortName: "TMUA",
  provider: "UAT-UK / Pearson VUE",
  status: "active",
  domains: ["tmua.fyi"],
  modulesSat: { min: 2, max: 2 },
  modules: [
    {
      id: "paper1",
      name: "Paper 1 — Applications of Mathematical Knowledge",
      shortName: "Paper 1",
      classification: "skill",
      questionCount: 20,
      durationMinutes: 75,
      mandatory: true,
    },
    {
      id: "paper2",
      name: "Paper 2 — Mathematical Reasoning",
      shortName: "Paper 2",
      classification: "skill",
      questionCount: 20,
      durationMinutes: 75,
      mandatory: true,
    },
  ],
  scoring: UAT_UK_SCORING,
  // Estimated: no official Pearson-era conversion is public. Anchors are the
  // only public raw→scaled table (2023 old-scale, CUP&A FOI release): 14/40
  // (35%) → 4.5 and 26/40 (65%) → 7.0. National percentiles are the UAT-UK
  // October 2024/25 cohort quantiles. Both via the tmua.fyi annual report.
  scoreConversion: {
    anchors: [
      { pct: 35, band: 4.5 },
      { pct: 65, band: 7.0 },
    ],
    nationalPercentiles: [
      { percentile: 25, band: 3.3 },
      { percentile: 50, band: 4.5 },
      { percentile: 75, band: 5.7 },
      { percentile: 90, band: 7.0 },
    ],
    reference: "estimated from public TMUA data (October cohort)",
  },
  universities: [
    {
      name: "Cambridge",
      fullName: "University of Cambridge",
      logo: "/cambridge.png",
      requirement: {
        slug: "cambridge",
        status: "required",
        overview:
          "Cambridge requires the TMUA for Computer Science, Economics and Mathematics. A strong result strengthens an application that is decided holistically: your predicted grades, personal statement, school reference and interview all count alongside the test. Mathematics applicants also sit STEP as part of their conditional offer. Cambridge applicants take the TMUA in the October window.",
        courses: [
          { name: "Computer Science", slug: "computer-science" },
          { name: "Economics", slug: "economics" },
          { name: "Mathematics", slug: "mathematics" },
        ],
        sitting: "Cambridge applicants must sit the TMUA in the October sitting.",
        officialUrl: "https://www.undergraduate.study.cam.ac.uk/apply/how/maths-admission-test",
      },
    },
    {
      name: "Imperial",
      fullName: "Imperial College London",
      logo: "/imperial.png",
      requirement: {
        slug: "imperial",
        status: "required",
        overview:
          "Imperial College London requires the TMUA for its Computing and Mathematics degrees and for BSc Economics, Finance and Data Science, and weighs the score directly when deciding who to make offers to. A strong performance carries real weight here. You can sit either the October or January test. Imperial uses the ESAT for its engineering degrees and the TMUA for these maths, computing and data courses.",
        courses: [
          { name: "Computing", slug: "computing" },
          { name: "Mathematics", slug: "mathematics" },
          { name: "Mathematics and Computer Science", slug: "mathematics-and-computer-science" },
          { name: "Economics, Finance and Data Science", slug: "economics-finance-and-data-science" },
        ],
        sitting: "Imperial accepts either the October or January sitting.",
        officialUrl:
          "https://www.imperial.ac.uk/study/apply/undergraduate/process/admissions-tests/tmua/",
      },
    },
    {
      name: "Warwick",
      fullName: "University of Warwick",
      logo: "/warwick.png",
      requirement: {
        slug: "warwick",
        status: "required",
        overview:
          "Warwick requires the TMUA for Computer Science and Mathematics, where it is used holistically alongside your grades (STEP is an accepted alternative for Mathematics). For Economics, MORSE and related degrees the TMUA is optional, but a strong score can earn a reduced offer. Warwick sets its score thresholds after results each year and does not publish them in advance. You can sit in either October or January.",
        courses: [
          { name: "Computer Science", slug: "computer-science" },
          { name: "Mathematics", slug: "mathematics" },
        ],
        sitting: "You can sit in October or January. STEP is an accepted alternative for Mathematics.",
        officialUrl: "https://warwick.ac.uk/study/undergraduate/apply/tmua/",
      },
    },
    {
      name: "Durham",
      fullName: "Durham University",
      logo: "/durham.png",
      requirement: {
        slug: "durham",
        status: "recommended",
        overview:
          "Durham recommends rather than requires the TMUA for its Mathematics degrees. If you score 5.0 or above you are automatically considered for an offer one grade below the standard (A*AA instead of A*A*A); a lower score is still viewed positively and is never counted against you. You must authorise Durham to access your result. That makes sitting the test a low-risk way to strengthen your application. You can sit in either October or January.",
        courses: [{ name: "Mathematics", slug: "mathematics" }],
        sitting: "You can sit in October or January. A score of 5.0 or above earns consideration for a reduced offer.",
        officialUrl: "https://www.durham.ac.uk/departments/academic/mathematical-sciences/",
      },
    },
    {
      name: "LSE",
      fullName: "London School of Economics",
      logo: "/lse.png",
      requirement: {
        slug: "lse",
        status: "required",
        overview:
          "The London School of Economics requires the TMUA for BSc Economics and BSc Econometrics and Mathematical Economics, and recommends it for its other maths and economics degrees. LSE selects from an exceptionally strong applicant pool without interviews, so the TMUA is one of the few objective signals it has, and it is read carefully alongside your grades and personal statement. You can sit in either October or January.",
        courses: [
          { name: "Economics", slug: "economics" },
          { name: "Econometrics and Mathematical Economics", slug: "econometrics-and-mathematical-economics" },
        ],
        sitting: "You can sit in October or January.",
        officialUrl:
          "https://www.lse.ac.uk/study-at-lse/Undergraduate/Prospective-Students/How-to-Apply/Test-of-Mathematics-for-University-Admission-TMUA",
      },
    },
    {
      name: "UCL",
      fullName: "University College London",
      logo: "/ucl.png",
      requirement: {
        slug: "ucl",
        status: "required",
        overview:
          "UCL requires the TMUA for its Economics BSc (Econ) and Economics with a Year Abroad degrees. It is not used for other UCL courses. The score is read alongside the rest of your UCAS application when UCL selects from a large and competitive field. You can sit in either October or January.",
        courses: [
          { name: "Economics (BSc Econ)", slug: "economics" },
          { name: "Economics with a Year Abroad", slug: "economics-with-a-year-abroad" },
        ],
        sitting: "You can sit in October or January.",
        officialUrl: "https://www.ucl.ac.uk/prospective-students/undergraduate/degrees/economics-bsc-econ-2026",
      },
    },
  ],
  theme: "tmua",
  pastPaperSources: ["TMUA", "ECAA"],
  registration: {
    entry: "2027",
    opens: "2026-07-20",
    deadline: "2026-09-28",
    results: "November 2026",
  },
  sittings: [
    { key: "october", label: "October sitting", date: "2026-10-12" },
    {
      key: "january",
      label: "January sitting",
      date: "2027-01-04",
      note: "Only for mature-college Cambridge or Oxford Foundation Year applicants with a January deadline.",
    },
  ],
};

/**
 * Retired (2023). Modelled so historical NSAA content and NSAA→ESAT/TMUA topic
 * mappings remain first-class. Not served on any live domain.
 */
const NSAA: ExamBoardConfig = {
  id: "NSAA",
  slug: "nsaa",
  name: "Natural Sciences Admissions Assessment",
  shortName: "NSAA",
  provider: "Cambridge Assessment Admissions Testing (retired)",
  status: "retired",
  domains: [],
  modulesSat: { min: 2, max: 2 },
  modules: [
    {
      id: "section1",
      name: "Section 1 — Mathematics + chosen science",
      shortName: "Section 1",
      classification: "subject",
      questionCount: 40,
      durationMinutes: 60,
      mandatory: true,
    },
    {
      id: "section2",
      name: "Section 2 — Advanced chosen science",
      shortName: "Section 2",
      classification: "subject",
      questionCount: 20,
      durationMinutes: 60,
      mandatory: true,
    },
  ],
  scoring: {
    min: 1.0,
    max: 9.0,
    step: 0.1,
    negativeMarking: false,
    calculatorAllowed: false,
  },
  universities: [
    { name: "Cambridge", fullName: "University of Cambridge", logo: "/cambridge.png" },
  ],
  theme: "tmua",
  pastPaperSources: ["NSAA"],
};

export const EXAM_BOARDS = { TMUA, NSAA } as const satisfies Record<
  ExamBoardId,
  ExamBoardConfig
>;

/** The board served when a host can't be resolved (local dev, previews, etc.). */
export const DEFAULT_BOARD_ID: ExamBoardId = "TMUA";
